import { ICommonSettings, IStripeSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigStripeSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingStripeSettings?: IStripeSetting;
}

export interface IConfigStripeSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingStripeId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigStripeSettings extends React.PureComponent<IConfigStripeSettingsProps, IConfigStripeSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigStripeSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadStripeSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigStripeSettingsProps) {
    if (prevProps.editingStripeId && !this.props.editingStripeId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingStripeId !== this.props.editingStripeId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadStripeSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadStripeSetting();
    }
  }

  loadStripeSetting = () => {
    if (!this.props.editingStripeId) return;

    const editingStripeSettings = this.props.commonSettings.stripeSettings?.find(
      (x) => x.id === this.props.editingStripeId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingStripeSettings) return;
    this.setState({ editingStripeSettings }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingStripeSettings);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const stripeSetting = this.props.commonSettings?.stripeSettings;
    if (!stripeSetting) return;

    const isSettingAlreadyExist = stripeSetting.some(
      (x) => x.name === value && x.id !== this.props.editingStripeId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Stripe Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IStripeSetting;
    if (this.props.editingStripeId) {
      obj = {
        id: this.props.editingStripeId,
        name: data.name,
        publishKey: data.publishKey,
        secretKey: data.secretKey,
        webhookSecret: data.webhookSecret,
        title: data.title
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "stripeSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        publishKey: data.publishKey?.trim(),
        secretKey: data.secretKey?.trim(),
        webhookSecret: data.webhookSecret?.trim(),
        title: data.title?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "stripeSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteStripe = () => {
    if (!this.props.editingStripeId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingStripeId || "",
      configKey: "stripeSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Stripe setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Stripe setting"
        secondHeader="Stripe setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingStripeId ? this.deleteStripe : undefined}
            deleteTestId="stripeSettingDeleteButton"
            submitTestId="stripeSettingCreateButton"
            submitLabel={this.props.editingStripeId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configStripeForm"
          />
        }
      >
        <Form
          id="configStripeForm"
          name="stripeSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("stripesettingspopup_stripesettings_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="stripesettingspopup_stripesettings_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a Stripe Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Stripe Setting Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="stripeSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Title" tooltipContentKey="stripesettingspopup_stripesettings_title">
                <Form.Item name="title">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Publish Key" tooltipContentKey="stripesettingspopup_stripesettings_publishkey">
                <Form.Item name="publishKey">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Secret Key" tooltipContentKey="stripesettingspopup_stripesettings_secretkey">
                <Form.Item name="secretKey">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Webhook Secret" tooltipContentKey="stripesettingspopup_stripesettings_webhooksecret">
                <Form.Item name="webhookSecret">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
